import React, { useEffect, useRef } from "react";

const loadStyle = (href) => {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
};

const loadScript = (src) =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.body.appendChild(script);
  });

const QuillEditor = ({
  theme = "snow",
  value = "",
  onChange,
  height = 150,
  placeholder = "",
}) => {
  const editorRef = useRef(null);
  const quillRef = useRef(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Load Quill CSS
    loadStyle("/assets/plugins/quill/quill.core.css");
    loadStyle("/assets/plugins/quill/quill.snow.css");
    loadStyle("/assets/plugins/quill/quill.bubble.css");

    // Load Quill JS
    loadScript("/assets/plugins/quill/quill.min.js").then(() => {
      const Quill = window.Quill;
      if (!Quill || !editorRef.current || hasInitialized.current) return;

      const toolbarOptions = [
        ["bold", "italic", "underline", "strike"],
        ["blockquote", "code-block"],
        [{ header: 1 }, { header: 2 }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ script: "sub" }, { script: "super" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ direction: "rtl" }],
        [{ size: ["small", false, "large", "huge"] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ color: [] }, { background: [] }],
        [{ font: [] }],
        [{ align: [] }],
        [{ lineheight: ["1", "1.5", "2", "2.5", "3"] }],
        ["link", "image", "video"],
        ["clean"],
      ];

      quillRef.current = new Quill(editorRef.current, {
        theme,
        placeholder,
        modules: { toolbar: toolbarOptions },
      });

      // Set initial value right away
      if (value) {
        quillRef.current.clipboard.dangerouslyPasteHTML(value);
      }

      // Listen for text changes
      quillRef.current.on("text-change", () => {
        const html = quillRef.current.root.innerHTML;
        if (onChange) onChange(html);
      });

      hasInitialized.current = true;
    });

    return () => {
      quillRef.current = null;
      hasInitialized.current = false;
    };
  }, []);

  // Update editor content if `value` changes externally
  useEffect(() => {
    if (
      quillRef.current &&
      hasInitialized.current &&
      value !== quillRef.current.root.innerHTML
    ) {
      quillRef.current.clipboard.dangerouslyPasteHTML(value);
    }
  }, [value]);

  return <div ref={editorRef} style={{ height }} />;
};

export default QuillEditor;
